import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { aiDashboardService } from '../services/aiDashboardService';
import { authService } from '../services';
import { useAICopilot } from '../hooks/useAICopilot';
import AICopilotPanel from '../components/ai-dashboard/AICopilotPanel';
import AIInsightCard from '../components/ai-dashboard/AIInsightCard';
import DynamicChart from '../components/ai-dashboard/DynamicChart';
import { HiChat, HiLightningBolt, HiClipboardList, HiShieldCheck, HiTrendingUp, HiExclamationCircle, HiBookmark, HiOutlineSparkles } from 'react-icons/hi';
import PageLoading from '../components/common/PageLoading';
import AISkeleton from '../components/ai-dashboard/AISkeleton';

import './AIDashboard.css';

// Simple inner skeleton component
const Skeleton = ({ className = '' }) => <div className={`skeleton-pulse ${className}`} />;

const AIDashboard = () => {
  const navigate = useNavigate();
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [kpiSummary, setKpiSummary] = useState(null);
  const [insights, setInsights] = useState([]);
  const [isCopilotExpanded, setIsCopilotExpanded] = useState(window.innerWidth > 1200);

  const copilot = useAICopilot();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingInitial(true);
        
        // Task 3.1 cache handling
        const cachedSummary = sessionStorage.getItem('ai_kpi_summary');
        if (cachedSummary) {
           setKpiSummary(JSON.parse(cachedSummary));
        }

        const [kpiData, insightsData] = await Promise.allSettled([
          aiDashboardService.getKpiSummary(),
          aiDashboardService.getInsights()
        ]);
        
        if (kpiData.status === 'fulfilled') {
            setKpiSummary(kpiData.value);
            sessionStorage.setItem('ai_kpi_summary', JSON.stringify(kpiData.value));
        }
        if (insightsData.status === 'fulfilled') setInsights(insightsData.value);
      } catch (err) {
        console.error("Dashboard init error", err);
      } finally {
        setLoadingInitial(false);
      }
    };

    fetchData();

    const handleResize = () => {
      if (window.innerWidth <= 1200 && isCopilotExpanded) setIsCopilotExpanded(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleActionClick = (action) => {
    if (action && action.route) {
      navigate(action.route, { state: action.prefill || {} });
    }
  };

  const handleDismissInsight = (id) => {
    setInsights(prev => prev.filter(i => i.id !== id));
  };

  if (loadingInitial && !kpiSummary) {
    return (
      <div className="ai-dashboard-container page-container fade-in">
        <div className="ai-dashboard-content custom-scrollbar">
          <AISkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="ai-dashboard-container page-container fade-in transition-all duration-500">
      <div className="ai-dashboard-content custom-scrollbar">
        
        {/* Header */}
        <div className="welcome-section">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="premium-gradient-text text-3xl font-extrabold tracking-tight">
                Welcome back, {authService.getUser()?.fullName?.split(' ')[0] || 'MaX User'}!
              </h2>
              <p className="text-secondary mt-1 text-base">Your MaX Elite AI has analyzed latest plant metrics and prepared these insights.</p>
            </div>
            <div className="ai-status-pill">
              <HiOutlineSparkles className="text-indigo-500" />
              <span>MaX Elite AI Online</span>
            </div>
          </div>
        </div>

        {/* LAYER 1: KPI STRIP */}
        <div className="kpi-strip">
          {loadingInitial && !kpiSummary ? (
            Array(5).fill(0).map((_, i) => (
              <div key={i} className="stat-card p-4">
                <Skeleton className="h-4 w-24 mb-4" />
                <Skeleton className="h-8 w-16" />
              </div>
            ))
          ) : (
            <>
              <div className="kpi-card accent-oee">
                <span className="kpi-label">Plant OEE</span>
                <div className="kpi-value">
                  {kpiSummary?.oee != null ? `${kpiSummary.oee}%` : '—'}
                </div>
                <div className="kpi-trend trend-up">
                  <HiTrendingUp /> +1.2% this week
                </div>
              </div>

              <div className="kpi-card accent-workorder">
                <span className="kpi-label">Open Work Orders</span>
                <div className="kpi-value">
                  {kpiSummary?.openWorkOrders != null ? kpiSummary.openWorkOrders : '—'}
                </div>
                <div className="kpi-trend text-secondary">
                  <HiClipboardList /> Live data
                </div>
              </div>

              <div className="kpi-card accent-compliance">
                <span className="kpi-label">PM Compliance</span>
                <div className="kpi-value">
                  {kpiSummary?.pmCompliance != null ? `${kpiSummary.pmCompliance}%` : '—'}
                </div>
                <div className="kpi-trend trend-up">
                  <HiShieldCheck /> Target 95%
                </div>
              </div>

              <div className="kpi-card accent-safety">
                <span className="kpi-label">Safety Score</span>
                <div className="kpi-value">
                  {kpiSummary?.safetyScore != null ? kpiSummary.safetyScore : '—'}
                </div>
                <div className="kpi-trend trend-up">
                  <HiShieldCheck /> 0 Incidents
                </div>
              </div>

              <div className="kpi-card accent-actions">
                <span className="kpi-label">Pending Actions</span>
                <div className="kpi-value">
                  {kpiSummary?.pendingActions != null ? kpiSummary.pendingActions : '—'}
                </div>
                <div className="kpi-trend trend-down">
                  <HiExclamationCircle /> High priority
                </div>
              </div>
            </>
          )}
        </div>

        {/* LAYER 2: PINNED CHARTS & INSIGHTS */}
        <div className="layer2-section">
          
          {copilot.pinnedCharts.length > 0 && (
            <div className="pinned-charts-section">
              <h3 className="section-title">
                <div className="section-icon bg-amber-500/10 text-amber-500">
                  <HiBookmark />
                </div>
                Pinned Analytics
              </h3>
              <div className="charts-grid custom-scrollbar">
                {copilot.pinnedCharts.map(item => (
                  <div key={item.id} className="bi-card pinned-card">
                    <div className="pinned-card-header">
                      <h4 className="m-0 text-[15px] font-semibold text-primary">{item.chartConfig.title}</h4>
                      <button className="unpin-btn" onClick={() => copilot.unpinChart(item.id)} title="Unpin">×</button>
                    </div>
                    <div className="pinned-card-body">
                      <DynamicChart config={item.chartConfig} compact={true} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {loadingInitial && insights.length === 0 ? (
             <div className="insights-section mt-6">
                <h3 className="section-title">
                  <span className="text-indigo-500 mr-2">🤖</span> AI Insights
                </h3>
                <div className="insights-grid">
                  <div className="bi-card p-4 h-[480px]">
                    <Skeleton className="h-6 w-3/4 mb-6" />
                    <Skeleton className="h-64 w-full mb-4" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                  <div className="bi-card p-4 h-[480px]">
                    <Skeleton className="h-6 w-3/4 mb-6" />
                    <Skeleton className="h-64 w-full mb-4" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                </div>
             </div>
          ) : insights.length > 0 && (
            <div className="insights-section mt-6">
              <h3 className="section-title">
                <div className="section-icon bg-indigo-500/10 text-indigo-500">
                  <HiLightningBolt />
                </div>
                Smart AI Insights
              </h3>
              <div className="insights-grid">
                {insights.map(insight => (
                  <AIInsightCard 
                    key={insight.id} 
                    insight={insight} 
                    onDismiss={handleDismissInsight}
                    onPin={copilot.pinChart}
                    isPinned={copilot.pinnedCharts.some(p => p.chartConfig.title === insight.chartConfig.title)}
                    onActionClick={handleActionClick}
                  />
                ))}
              </div>
            </div>
          )}

        </div>

      </div>

      {!isCopilotExpanded && (
        <button className="mobile-fab" onClick={() => setIsCopilotExpanded(true)}>
          <HiChat /> <span className="ai-status pulse-dot ml-2" />
        </button>
      )}

      {/* LAYER 3: COPILOT PANEL */}
      <AICopilotPanel
        isExpanded={isCopilotExpanded}
        onToggleExpand={() => setIsCopilotExpanded(!isCopilotExpanded)}
        messages={copilot.messages}
        loading={copilot.loading}
        suggestedQueries={copilot.suggestedQueries}
        onSendMessage={copilot.sendMessage}
        onRetry={copilot.retryMessage}
        onPin={copilot.pinChart}
        pinnedChartIds={copilot.pinnedCharts.map(c => c.id)}
        onClearChat={copilot.clearChat}
        onActionClick={handleActionClick}
      />
    </div>
  );
};

export default AIDashboard;
